import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import InvestorLayout from '../components/InvestorLayout';
import investorService from '../services/investorService';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Download, Filter, Receipt, AlertCircle, RefreshCw } from 'lucide-react';

const ITEMS_PER_PAGE = 20;

const InvestorTransactionsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [investmentFilter, setInvestmentFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // İşlem tipi badge renkleri
  const getTransactionTypeColor = (type) => {
    const colors = {
      deposit: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
      installment: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
      final_payment: 'bg-green-100 text-green-700 hover:bg-green-200',
      refund: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
      commission: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
      tax: 'bg-red-100 text-red-700 hover:bg-red-200',
      other: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  // Durum badge renkleri
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
      completed: 'bg-green-100 text-green-700 hover:bg-green-200',
      failed: 'bg-red-100 text-red-700 hover:bg-red-200',
      cancelled: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Para birimi sembolü
  const getCurrencySymbol = (currency) => {
    const symbols = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      TRY: '₺',
    };
    return symbols[currency] || currency;
  };

  const fetchData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const [transactionsResult, investmentsResult] = await Promise.all([
        investorService.getTransactions(user.id),
        investorService.getInvestments(user.id),
      ]);

      if (transactionsResult.error) throw transactionsResult.error;
      if (investmentsResult.error) throw investmentsResult.error;

      setTransactions(transactionsResult.data || []);
      setInvestments(investmentsResult.data || []);

    } catch (err) {
      console.error('Transactions fetch error:', err);
      setError(err.message || t('investor.dashboard.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [user?.id, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtreleme
  useEffect(() => {
    let filtered = [...transactions];

    // Durum filtreleme
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Yatırım filtreleme
    if (investmentFilter !== 'all') {
      filtered = filtered.filter(t => t.investment_id === investmentFilter);
    }

    setFilteredTransactions(filtered);
    setCurrentPage(1); // Filtre değişince ilk sayfaya dön
  }, [transactions, statusFilter, investmentFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Toplam tutar hesaplama (sadece completed işlemler)
  const totalAmount = filteredTransactions
    .filter(t => t.status === 'completed')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

  // Makbuz indirme
  const handleDownloadReceipt = async (transaction) => {
    // TODO: Makbuz indirme implementasyonu
    console.log('Downloading receipt for:', transaction.id);
    window.open(transaction.receipt_url, '_blank');
  };

  // Tablo skeleton
  const TableSkeleton = () => (
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center space-x-4 p-3 border-b">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-20" />
          <Skeleton className="h-10 w-20" />
        </div>
      ))}
    </div>
  );

  if (error) {
    return (
      <InvestorLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <AlertCircle size={48} className="text-red-500 mb-4" />
          <h2 className="text-2xl font-heading text-[#0A192F] mb-2">
            {t('investor.dashboard.error_title')}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button
            onClick={fetchData}
            className="bg-[#0A192F] text-white hover:bg-[#112240]"
          >
            <RefreshCw size={20} className="mr-2" />
            {t('investor.dashboard.retry')}
          </Button>
        </div>
      </InvestorLayout>
    );
  }

  return (
    <InvestorLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-heading text-[#0A192F] mb-2">
            {t('transactions.title')}
          </h1>
          <p className="text-gray-600">
            {t('investor.dashboard.welcome')}
          </p>
        </div>

        {/* Özet Kartı */}
        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  {t('transactions.totalAmount')}
                </p>
                <p className="text-3xl font-bold text-[#0A192F] mt-1">
                  £{totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Receipt size={32} className="text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filtreler */}
        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Durum Filtresi */}
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('transactions.filterByStatus')}
                </label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full">
                    <Filter size={16} className="mr-2 text-gray-400" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('transactions.all')}</SelectItem>
                    <SelectItem value="pending">{t('investor.document_status.pending')}</SelectItem>
                    <SelectItem value="completed">{t('investor.status.completed')}</SelectItem>
                    <SelectItem value="failed">{t('investor.permit_status.rejected')}</SelectItem>
                    <SelectItem value="cancelled">{t('investor.status.cancelled')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Yatırım Filtresi */}
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('transactions.filterByInvestment')}
                </label>
                <Select value={investmentFilter} onValueChange={setInvestmentFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('transactions.all')}</SelectItem>
                    {investments.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.property?.title || `Investment #${inv.id.slice(0, 8)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* İşlem Tablosu */}
        <Card className="border border-gray-200">
          <CardHeader className="border-b border-gray-100">
            <CardTitle className="text-lg font-heading text-[#0A192F]">
              {t('transactions.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6">
                <TableSkeleton />
              </div>
            ) : filteredTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('transactions.date')}</TableHead>
                      <TableHead>{t('transactions.type')}</TableHead>
                      <TableHead>{t('transactions.amount')}</TableHead>
                      <TableHead>{t('transactions.status')}</TableHead>
                      <TableHead>{t('transactions.reference')}</TableHead>
                      <TableHead className="text-right">{t('transactions.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTransactions.map((transaction) => (
                      <TableRow key={transaction.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          {new Date(transaction.transaction_date).toLocaleDateString('tr-TR')}
                        </TableCell>
                        <TableCell>
                          <Badge className={getTransactionTypeColor(transaction.transaction_type)}>
                            {t(`transactions.types.${transaction.transaction_type}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold text-[#0A192F]">
                          {getCurrencySymbol(transaction.currency)}{parseFloat(transaction.amount || 0).toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(transaction.status)}>
                            {transaction.status === 'completed'
                              ? t('investor.document_status.approved')
                              : transaction.status === 'failed'
                                ? t('investor.permit_status.rejected')
                                : transaction.status === 'cancelled'
                                  ? t('investor.status.cancelled')
                                  : t('investor.document_status.pending')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {transaction.reference_number || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {transaction.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadReceipt(transaction)}
                              className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white"
                            >
                              <Download size={14} className="mr-1" />
                              {t('transactions.downloadReceipt')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Receipt size={56} className="text-gray-300 mb-4" />
                <h3 className="text-xl font-heading text-[#0A192F] mb-2">
                  {t('transactions.noTransactions')}
                </h3>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              &larr; {t('common.view')}
            </Button>
            <span className="text-sm text-gray-600">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              {t('common.view')} &rarr;
            </Button>
          </div>
        )}
      </div>
    </InvestorLayout>
  );
};

export default InvestorTransactionsPage;
